import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { Copy, Check, Printer, AlertCircle } from "lucide-react";

import Layout from "../components/Layout";
import Breadcrumb from "../components/Breadcrumb";
import { FrameworkSkeleton } from "../components/Skeletons";
import MermaidBlock from "../components/MermaidBlock";
import DiagramBlock from "../components/DiagramBlock";
import BookmarkButton from "../components/BookmarkButton";
import { getProblem, generateFramework } from "../lib/api";
import NotFoundPage from "./NotFoundPage";

const SS_KEY = (a, b) => `fch:${a}:${b}`;

function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    if (signal) {
      signal.addEventListener("abort", () => {
        clearTimeout(t);
        reject(new DOMException("aborted", "AbortError"));
      });
    }
  });
}

async function pollForCache(industrySlug, problemSlug, signal, attempts = 6, intervalMs = 5000) {
  for (let i = 0; i < attempts; i++) {
    if (signal?.aborted) return null;
    try {
      await sleep(intervalMs, signal);
    } catch (_) {
      return null;
    }
    if (signal?.aborted) return null;
    try {
      const detail = await getProblem(industrySlug, problemSlug);
      if (detail.cached) return detail.cached;
    } catch (_) {
      // keep polling
    }
  }
  return null;
}

function estimateReadingMinutes(text) {
  if (!text) return 1;
  const words = String(text).trim().split(/\s+/).length;
  return Math.max(1, Math.round(words / 220));
}

export default function ProblemPage() {
  const { industrySlug, problemSlug } = useParams();
  const [industry, setIndustry] = useState(null);
  const [problem, setProblem] = useState(null);
  const [cached, setCached] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(null);
  const [rateLimited, setRateLimited] = useState(false);
  const [copied, setCopied] = useState(false);
  const abortRef = useRef(null);

  useEffect(() => {
    // Abort any previous in-flight work
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;
    const { signal } = controller;

    // Reset state synchronously on every route change
    setLoading(true);
    setGenerating(false);
    setNotFound(false);
    setError(null);
    setRateLimited(false);
    setCached(null);
    setIndustry(null);
    setProblem(null);

    (async () => {
      try {
        const detail = await getProblem(industrySlug, problemSlug);
        if (signal.aborted) return;
        setIndustry(detail.industry);
        setProblem(detail.problem);

        if (detail.cached) {
          setCached(detail.cached);
          setLoading(false);
          return;
        }

        const ssKey = SS_KEY(detail.industry.id, detail.problem.id);
        const ss = sessionStorage.getItem(ssKey);
        if (ss) {
          try {
            setCached(JSON.parse(ss));
            setLoading(false);
            return;
          } catch (_) {
            // fall through
          }
        }

        setLoading(false);
        setGenerating(true);
        try {
          const gen = await generateFramework(detail.industry.id, detail.problem.id);
          if (signal.aborted) return;
          const out = gen.cached || gen;
          setCached(out);
          sessionStorage.setItem(ssKey, JSON.stringify(out));
        } catch (genErr) {
          if (signal.aborted) return;
          const status = genErr?.response?.status;
          if (status === 502 || status === 504 || !status) {
            const recovered = await pollForCache(industrySlug, problemSlug, signal);
            if (signal.aborted) return;
            if (recovered) {
              setCached(recovered);
              sessionStorage.setItem(ssKey, JSON.stringify(recovered));
            } else {
              throw genErr;
            }
          } else {
            throw genErr;
          }
        }
      } catch (e) {
        if (signal.aborted) return;
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail || e.message;
        if (status === 404) setNotFound(true);
        else if (status === 429) setRateLimited(detail);
        else setError(detail);
      } finally {
        if (!signal.aborted) {
          setLoading(false);
          setGenerating(false);
        }
      }
    })();

    return () => {
      controller.abort();
    };
  }, [industrySlug, problemSlug]);

  const handleCopy = async () => {
    if (!cached) return;
    const text = `# ${cached.framework_name}\n\n_${cached.why_this_framework}_\n\n${cached.tailored_content}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      // noop
    }
  };

  if (notFound) return <NotFoundPage />;

  const readingMin = cached ? estimateReadingMinutes(cached.tailored_content) : null;

  return (
    <Layout>
      <section className="max-w-4xl mx-auto px-6 md:px-10 pt-10 md:pt-12 pb-6 print:pt-0">
        <Breadcrumb
          items={[
            { label: "Directory", to: "/" },
            { label: industry?.name || "…", to: industry ? `/${industry.slug}` : undefined },
            { label: problem?.title || "…" },
          ]}
        />
      </section>

      <article className="max-w-4xl mx-auto px-6 md:px-10 pb-24 print:pb-8">
        {loading || !problem ? (
          <div className="border-l-2 border-ink-200 pl-6 py-3 my-6 space-y-3">
            <div className="skeleton h-3 w-24" />
            <div className="skeleton h-8 w-3/4" />
          </div>
        ) : (
          <motion.section
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            className="border-l-2 border-ink-900 pl-6 py-1 my-4 page-break-avoid"
            data-testid="problem-block"
          >
            <div className="flex items-center gap-3 flex-wrap mb-3">
              <span className="font-mono text-[10px] uppercase tracking-wide-3 text-ink-500">
                Problem · IND.{String(industry?.id).padStart(2, "0")}.{String(problem.problem_number).padStart(2, "0")}
              </span>
              <span className="hairline w-4" />
              <span className="font-mono text-[10px] uppercase tracking-wide-3 text-ink-500">
                {industry?.name}
              </span>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-semibold text-ink-900 leading-[1.1] tracking-[-0.025em]">
              {problem.title}
            </h1>
          </motion.section>
        )}

        <div className="hairline my-12 print:hidden" />

        {rateLimited && (
          <div
            className="border border-ink-300 bg-ink-100 p-6 md:p-8"
            data-testid="rate-limit-block"
          >
            <div className="flex items-start gap-4">
              <AlertCircle className="text-ink-900 mt-1 shrink-0" size={18} />
              <div>
                <p className="font-mono text-[10px] uppercase tracking-wide-3 text-ink-500">
                  Daily quota
                </p>
                <h2 className="mt-2 font-heading text-2xl text-ink-900 font-semibold">
                  Allowance exhausted for the day.
                </h2>
                <p className="mt-2 text-ink-600 leading-relaxed">{rateLimited}</p>
              </div>
            </div>
          </div>
        )}

        {error && !rateLimited && (
          <div className="border border-ink-300 bg-bone p-6" data-testid="problem-error">
            <p className="font-mono text-[10px] uppercase tracking-wide-3 text-ink-500">Error</p>
            <p className="mt-2 font-medium text-ink-900">The recommendation could not be generated.</p>
            <p className="text-sm text-ink-600 mt-1">{String(error)}</p>
          </div>
        )}

        {(loading || generating) && !rateLimited && !error && (
          <section data-testid="framework-loading">
            <p className="font-mono text-[10px] uppercase tracking-wide-3 text-ink-500 mb-4">
              {generating
                ? "Compiling industry-specific recommendation…"
                : "Loading reference…"}
            </p>
            <FrameworkSkeleton />
          </section>
        )}

        {!loading && !generating && cached && (
          <motion.section
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="framework-result"
          >
            <div className="page-break-avoid">
              <div className="flex items-center gap-3 flex-wrap mb-4">
                <span className="accent-marker" />
                <span className="font-mono text-[10px] uppercase tracking-wide-3 text-vermilion">
                  Recommended framework
                </span>
                {readingMin && (
                  <>
                    <span className="hairline w-4" />
                    <span className="font-mono text-[10px] uppercase tracking-wide-3 text-ink-500">
                      {readingMin} min read
                    </span>
                  </>
                )}
              </div>
              <h2
                className="font-heading text-4xl md:text-5xl font-semibold text-ink-900 tracking-[-0.03em] leading-[1.04]"
                data-testid="framework-name"
              >
                {cached.framework_name}
              </h2>
              <div className="mt-6 border-l-2 border-vermilion pl-6 pr-5 py-1">
                <p className="font-mono text-[10px] uppercase tracking-wide-3 text-ink-500 mb-2">
                  Why this framework
                </p>
                <p
                  className="text-ink-700 leading-relaxed text-[17px] italic"
                  data-testid="why-this-framework"
                >
                  {cached.why_this_framework}
                </p>
              </div>
            </div>

            <div className="mt-12 prose-fch" data-testid="tailored-content">
              <ReactMarkdown
                components={{
                  code({ className, children, ...rest }) {
                    const text = String(children || "").replace(/\n$/, "");
                    const lang = /language-(\w+)/.exec(className || "")?.[1];
                    if (lang === "diagram") {
                      return <DiagramBlock code={text} />;
                    }
                    if (lang === "mermaid") {
                      return <MermaidBlock code={text} />;
                    }
                    return (
                      <code className={className} {...rest}>
                        {children}
                      </code>
                    );
                  },
                }}
              >
                {cached.tailored_content || ""}
              </ReactMarkdown>
            </div>

            <div className="print:hidden mt-14 flex flex-wrap items-center gap-3 border-t border-ink-200 pt-8">
              <button
                onClick={handleCopy}
                className="inline-flex items-center gap-2 px-7 py-3 bg-vermilion text-bone hover:bg-ink-900 hover:text-bone font-mono text-[11px] uppercase tracking-wide-2 transition-colors"
                data-testid="copy-button"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? "Copied" : "Copy reference"}
              </button>
              <button
                onClick={() => window.print()}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-bone text-ink-900 border border-ink-300 font-mono text-[11px] uppercase tracking-wide-2 hover:bg-ink-100 transition-colors"
                data-testid="print-button"
              >
                <Printer size={14} />
                Print / PDF
              </button>
              {problem && <BookmarkButton problemId={problem.id} />}
              <span className="ml-auto font-mono text-[10px] uppercase tracking-wide-3 text-ink-400">
                End of reference
              </span>
            </div>
          </motion.section>
        )}
      </article>
    </Layout>
  );
}
